import React from "react";
import { useParams } from "react-router-dom";
import { useState } from "react";
import type { AcceptDetail } from "@/lib/hiring-api";
import { useGetAcceptPage, useAcceptOffer } from "@/lib/hiring-api";
import { CheckCircle2, Loader2, Video, DollarSign, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import QRCode from "react-qr-code";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function renderWithLinksAndNewlines(text: string): React.ReactNode {
  const pattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  return text.split("\n").map((line, i) => {
    const parts: React.ReactNode[] = [];
    let last = 0;
    let m: RegExpExecArray | null;
    pattern.lastIndex = 0;
    while ((m = pattern.exec(line)) !== null) {
      if (m.index > last) parts.push(line.slice(last, m.index));
      parts.push(
        <a key={m.index} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-[#FB3AAE] underline break-all">
          {m[1]}
        </a>
      );
      last = m.index + m[0].length;
    }
    if (last < line.length) parts.push(line.slice(last));
    return (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {parts}
      </React.Fragment>
    );
  });
}

const PLACE_LABELS: Record<number, string> = {
  1: "1st Place", 2: "2nd Place", 3: "3rd Place", 4: "4th Place", 5: "5th Place",
  6: "6th Place", 7: "7th Place", 8: "8th Place", 9: "9th Place", 10: "10th Place",
};

const MEDAL_EMOJIS: Record<number, string> = { 1: "\uD83E\uDD47", 2: "\uD83E\uDD48", 3: "\uD83E\uDD49" };

const DEFAULT_CONFIRMATION_STEPS = [
  {
    title: "Request Your Sample",
    body: "Tap the button below to request your free product sample through TikTok Shop.",
    show_tap_button: true,
  },
  {
    title: "Create Your Videos",
    body: "Once your sample arrives, create {numVideos} videos featuring {productLabel}.\n\nA few things to keep in mind:\n- Please spread your videos out — don't post them all in one day\n- Let's target having all {numVideos} videos posted within 28 days",
    show_tap_button: false,
  },
  {
    title: "GET PAID",
    body: "Request Your Payment. Payments are processed through Trolley. Once your campaign is complete, you'll receive an email invite to connect your Trolley account (if you haven't already). From there, we process payouts as the campaign is running and at latest, shortly after campaign completion.",
    show_tap_button: false,
  },
];

const DEFAULT_CONFIRMATION_FOOTER = "Questions? Just ask support@brandsmeetcreators.com and our team will get back to you.";

export default function AcceptOffer() {
  const params = useParams();
  const token = params.token;
  const [accepted, setAccepted] = useState(false);
  const [tapModalOpen, setTapModalOpen] = useState(false);

  const { data, isLoading, error } = useGetAcceptPage(token);

  const acceptMutation = useAcceptOffer({
    mutation: {
      onSuccess: () => {
        setAccepted(true);
      },
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm max-w-md w-full p-8 text-center">
          <div className="text-4xl mb-4">{"\uD83D\uDD17"}</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Link Invalid</h2>
          <p className="text-gray-500 text-sm">
            This offer link is invalid or has already been used. Please check your email
            for the correct link.
          </p>
        </div>
      </div>
    );
  }

  const { offer, campaign } = data as AcceptDetail;
  const c = campaign as any;
  const alreadyAccepted = offer.status === "accepted" || accepted;

  const acceptHeadline = c.accept_headline || "You've been invited to create for";
  const acceptCtaText = c.accept_cta_text || "I'M IN \u2014 ACCEPT THIS DEAL";
  const acceptedTitle = c.accepted_title || "You're in!";
  const acceptedMessage = (c.accepted_message || "You've accepted the offer with {brand}. The BMC team will be in touch soon with next steps.")
    .replace(/\{brand\}/gi, campaign.brand_name);
  const bonusText = c.bonus_note_text || "";
  const hasRewards = c.has_rewards ?? false;
  const isRewardsCampaign = c.is_rewards_campaign ?? false;
  const prizePool: { place: number; amount: number }[] = c.prize_pool || [];
  const maxSpots: number | null = c.max_spots ?? null;

  const guaranteed = campaign.offer_amount * campaign.num_videos_per_creator;
  const firstPlaceAmount = prizePool.length > 0 ? prizePool[0].amount : 0;
  const totalPrize = prizePool.reduce((s, p) => s + p.amount, 0);

  const { steps, confirmationFooter } = (() => {
    try {
      const parsed = c.accepted_next_steps ? JSON.parse(c.accepted_next_steps) : null;
      if (!parsed) return { steps: DEFAULT_CONFIRMATION_STEPS, confirmationFooter: DEFAULT_CONFIRMATION_FOOTER };
      // New format: { steps, footer }
      if (!Array.isArray(parsed) && parsed.steps) {
        return {
          steps: Array.isArray(parsed.steps) && parsed.steps.length > 0 ? parsed.steps : DEFAULT_CONFIRMATION_STEPS,
          confirmationFooter: parsed.footer || DEFAULT_CONFIRMATION_FOOTER,
        };
      }
      // Old format: bare array
      return {
        steps: Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_CONFIRMATION_STEPS,
        confirmationFooter: DEFAULT_CONFIRMATION_FOOTER,
      };
    } catch {
      return { steps: DEFAULT_CONFIRMATION_STEPS, confirmationFooter: DEFAULT_CONFIRMATION_FOOTER };
    }
  })();
  const hasTapStep = steps.some((s: any) => s.show_tap_button);
  const firstTapStepIndex = steps.findIndex((s: any) => s.show_tap_button);
  const productLabel = campaign.product_name || campaign.brand_name;
  const resolveBody = (body: string) =>
    body
      .replace(/\{numVideos\}/gi, String(campaign.num_videos_per_creator))
      .replace(/\{productLabel\}/gi, productLabel)
      .replace(/\{brand\}/gi, campaign.brand_name)
      .replace(/\{guaranteed\}/gi, `$${guaranteed.toLocaleString()}`);

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="max-w-lg w-full space-y-4">
        <div className="text-center mb-2">
          <img src="/bmc-logo.png" alt="Brands Meet Creators" className="h-10 mx-auto" />
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {alreadyAccepted ? (
            <div className="p-8">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-3">
                  <div className="rounded-full bg-green-100 p-3">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{acceptedTitle}</h2>
                <p className="text-gray-500 text-sm whitespace-pre-wrap">{acceptedMessage}</p>
              </div>

              {campaign.tap_link && (
                <Dialog open={tapModalOpen} onOpenChange={(v) => !v && setTapModalOpen(false)}>
                  <DialogContent className="max-w-xs">
                    <DialogHeader>
                      <DialogTitle className="text-center text-base">Request Your Sample</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-4 py-2">
                      <p className="text-xs text-gray-500 text-center">Scan with your phone's camera, or tap the link below to open on any device.</p>
                      <div className="p-3 bg-white border border-slate-200 rounded-xl">
                        <QRCode value={campaign.tap_link} size={180} />
                      </div>
                      <a
                        href={campaign.tap_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs font-medium text-[#FB3AAE] hover:underline break-all text-center"
                      >
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                        Open link →
                      </a>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {hasTapStep && campaign.tap_link && (
                <div className="mb-5 bg-[#FB3AAE]/8 border border-[#FB3AAE]/25 rounded-xl px-4 py-3 flex items-start gap-3">
                  <span className="text-xl mt-0.5">👇</span>
                  <div>
                    <p className="text-sm font-bold text-gray-900">You're not done yet!</p>
                    <p className="text-xs text-gray-600 mt-0.5">Complete Step {firstTapStepIndex + 1} below to request your free product sample before you're all set.</p>
                  </div>
                </div>
              )}

              {steps.map((step: any, index: number) => (
                <div key={index}>
                  <hr className="border-slate-200 mb-5" />
                  <div className="mb-5">
                    <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-3">
                      Step {index + 1} — {step.title}
                    </p>
                    {step.body && (
                      <p className="text-sm text-gray-600 leading-relaxed mb-4 whitespace-pre-wrap">
                        {resolveBody(step.body)}
                      </p>
                    )}
                    {step.show_tap_button && campaign.tap_link && (
                      <button
                        type="button"
                        onClick={() => setTapModalOpen(true)}
                        className="w-full flex items-center justify-center gap-2 text-base font-bold text-white bg-[#FB3AAE] rounded-xl px-4 py-4 hover:bg-[#e02a9a] active:scale-[0.98] transition-all shadow-lg shadow-pink-200"
                      >
                        Request Sample on TikTok →
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <hr className="border-slate-200 mb-5" />
              <div className="mb-5">
                <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-3">Your Offer Recap</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Guaranteed pay:</span>
                    <span className="font-medium text-gray-900">${guaranteed.toLocaleString()} (${campaign.offer_amount}/vid × {campaign.num_videos_per_creator} videos)</span>
                  </div>
                  {hasRewards && firstPlaceAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Prize pool:</span>
                      <span className="font-medium text-gray-900">up to ${firstPlaceAmount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">TikTok Shop commission:</span>
                    <span className="font-medium text-gray-900">uncapped</span>
                  </div>
                  <div className="flex justify-between font-bold border-t border-slate-200 pt-2 mt-1">
                    <span className="text-gray-700">Potential total:</span>
                    <span className="text-[#FB3AAE]">${(guaranteed + (hasRewards ? firstPlaceAmount : 0)).toLocaleString()}+</span>
                  </div>
                </div>
              </div>

              {isRewardsCampaign && (
                <>
                  <hr className="border-slate-200 mb-5" />
                  <div className="mb-5">
                    <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-3">📊 Track Your Rankings</p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">
                      Check the leaderboard anytime to see where you stand against other creators.
                    </p>
                    <a
                      href={`/leaderboard/${token}`}
                      className="w-full flex items-center justify-center gap-2 text-base font-bold text-white bg-slate-800 rounded-xl px-4 py-3 hover:bg-slate-700 active:scale-[0.98] transition-all"
                    >
                      View Leaderboard →
                    </a>
                  </div>
                </>
              )}

              <hr className="border-slate-200 mb-5" />
              <p className="text-sm text-center text-gray-400">
                {confirmationFooter}
              </p>
            </div>
          ) : (
            <div className="p-8">
              <p className="text-sm text-gray-500 mb-1">{acceptHeadline}</p>
              <p className="text-2xl font-black text-gray-900 uppercase mb-1">
                {campaign.brand_name}
                {campaign.product_name && (
                  <span className="text-lg font-medium text-gray-500 ml-2 normal-case">
                    — {campaign.product_name}
                  </span>
                )}
              </p>
              <p className="text-sm text-gray-400 mb-4">This is a TikTok Shop affiliate campaign.</p>

              {campaign.tap_link && (
                <div className="mb-5 p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm font-semibold text-gray-800 mb-2">👀 Want to check out the product first?</p>
                  <a
                    href={campaign.tap_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-[#FB3AAE] font-medium hover:underline mb-2"
                  >
                    View Product on TikTok →
                  </a>
                  <p className="text-xs text-gray-500">⚠️ Opens in TikTok app. Come back here to accept.</p>
                  <p className="text-xs text-gray-500">Don't request a sample yet — accept the deal first.</p>
                </div>
              )}

              {hasRewards && totalPrize > 0 ? (
                <div className="mb-6">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-[#FB3AAE]/5 border border-[#FB3AAE]/20 rounded-xl p-4 text-center">
                      <p className="text-2xl mb-1">💰</p>
                      <p className="text-2xl font-bold text-[#FB3AAE]">${guaranteed.toLocaleString()}</p>
                      <p className="text-sm text-gray-500">guaranteed</p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                      <p className="text-2xl mb-1">🏆</p>
                      <p className="text-2xl font-bold text-yellow-700">${totalPrize.toLocaleString()}</p>
                      <p className="text-sm text-gray-500">in prizes</p>
                      {maxSpots && (
                        <p className="text-xs text-yellow-600 mt-1">~{maxSpots} creators</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                      <p className="text-base font-bold text-gray-700">${campaign.offer_amount}</p>
                      <p className="text-xs text-gray-500">per video</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                      <p className="text-base font-bold text-gray-700">{campaign.num_videos_per_creator}</p>
                      <p className="text-xs text-gray-500">videos</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <DollarSign className="h-5 w-5 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-700">${campaign.offer_amount}</p>
                    <p className="text-xs text-gray-500">per video</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <Video className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-blue-700">{campaign.num_videos_per_creator}</p>
                    <p className="text-xs text-gray-500">videos</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <DollarSign className="h-5 w-5 text-[#FB3AAE] mx-auto mb-1" />
                    <p className="text-lg font-bold text-[#FB3AAE]">${guaranteed.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">guaranteed</p>
                  </div>
                </div>
              )}

              {campaign.brief_requirements && (
                <div className="mb-5">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">
                    📋 Content Guidelines
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {renderWithLinksAndNewlines(campaign.brief_requirements)}
                  </p>
                </div>
              )}

              {campaign.additional_info && (
                <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed mb-5">
                  {campaign.additional_info}
                </p>
              )}

              <hr className="border-slate-200 mb-5" />

              <div className="mb-5">
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">
                  {"\uD83D\uDCB8"} TikTok Shop Commission
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {campaign.commission_text || `As a TikTok Shop affiliate, you earn commission on every sale your content drives — on top of your guaranteed pay.`}
                </p>
              </div>

              {hasRewards && prizePool.length > 0 && (
                <>
                  <hr className="border-slate-200 mb-5" />

                  <div className="mb-5">
                    <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">
                      {"\uD83C\uDFC6"} Bonus: Rewards Campaign
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-2">
                      On top of your guaranteed ${guaranteed.toLocaleString()}, compete for cash prizes based on your total GMV (sales generated) for {campaign.brand_name} by the end of the campaign.
                    </p>
                    {maxSpots && (
                      <p className="text-sm text-gray-600 leading-relaxed mb-4">
                        We're targeting around {maxSpots} creators for this campaign. This is a private rewards campaign only applicable to selected BMC creators — your odds of winning are real with only {maxSpots} participants.
                      </p>
                    )}

                    <div className="space-y-2 mb-3">
                      {prizePool.map((prize) => (
                        <div key={prize.place} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">
                            {MEDAL_EMOJIS[prize.place] || `#${prize.place}`} {PLACE_LABELS[prize.place] || `Place #${prize.place}`}
                          </span>
                          <span className="font-bold text-gray-900">${prize.amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>

                    <p className="text-sm text-gray-500">Total prize pool: ${totalPrize.toLocaleString()}</p>

                    {bonusText && (
                      <p className="text-sm text-gray-600 leading-relaxed mt-3">
                        {bonusText}
                      </p>
                    )}
                  </div>

                  <hr className="border-slate-200 mb-5" />

                  <div className="mb-6">
                    <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">
                      Your Total Earning Potential
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Guaranteed pay:</span>
                        <span className="font-medium text-gray-900">${guaranteed.toLocaleString()}</span>
                      </div>
                      {firstPlaceAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Prize pool:</span>
                          <span className="font-medium text-gray-900">up to ${firstPlaceAmount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">TikTok Shop commission:</span>
                        <span className="font-medium text-gray-900">uncapped</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2 mt-1">
                        <span className="text-gray-700">Potential total:</span>
                        <span className="text-[#FB3AAE]">${(guaranteed + firstPlaceAmount).toLocaleString()}+</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <hr className="border-slate-200 mb-5" />

              <Button
                className="w-full h-12 text-base font-bold bg-[#FB3AAE] hover:bg-[#E0309C]"
                onClick={() => acceptMutation.mutate({ token })}
                disabled={acceptMutation.isPending}
                data-testid="btn-accept-offer"
              >
                {acceptMutation.isPending ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : null}
                {acceptCtaText}
              </Button>
              <p className="text-xs text-center text-gray-400 mt-3">
                This link is unique to you. By accepting you agree to create content per the guidelines above.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
